import { useMutation, useQueryClient } from 'react-query';

import { saveDiagnosticsCases } from '@ge/feat-analyze/services';
import { QueryKey } from '@ge/models/constants';
import { CasesQueryKeys } from '@ge/shared/data-hooks/cases/query-keys';

import * as damageService from '../services/damages';

//calling API directly
// import * as fileService from '../services/downloadFiles';

export const useCreatePacCase = () => {
  const { mutate } = useMutation(
    (data) => (!data ? () => {} : damageService.createPacCase(data, {})),
    // (data) => (!data ? () => {} : fileService.createPacCase(data, {})),
  );
  return { mutate };
};

export const usePatchAnnotionJob = () => {
  const { mutate } = useMutation(({ annotationId, status }) =>
    !status && !annotationId
      ? () => {}
      : damageService.patchAnnotationJob(annotationId, status, {}),
  );
  return { mutate };
};

export const useCreateNewDamages = ({ onSuccess, caseId }) => {
  const queryClient = useQueryClient();
  const {
    mutateAsync: createDamages,
    isLoading,
    isError,
    error,
  } = useMutation(
    async ({ new: payload, existing }) => {
      const damagesToSave = [];
      if (payload.damages.length) {
        const newDamagesRes = await damageService.createNewDamage(payload);
        damagesToSave.push(
          ...newDamagesRes.damages.map((x, index) => {
            const { category, status, pacNumber, pacUrl } = payload.damages[index];
            return {
              category,
              status,
              pacNumber,
              pacUrl,
              damageId: x,
            };
          }),
        );
      }
      if (existing?.length) {
        damagesToSave.push(
          ...existing.map((x) => {
            const { id, category, status, pacNumber, pacUrl } = x;
            return {
              category,
              status,
              pacNumber,
              pacUrl,
              damageId: id,
            };
          }),
        );
      }
      if (damagesToSave.length) {
        // Second API call based on the result of the `newDamagesRes`
        await saveDiagnosticsCases(caseId, {
          action: 'addDamage',
          damages: damagesToSave,
        });
      }
      return true;
    },
    {
      onError: (error) => {
        console.log(`there has been an error ${error}`);
      },
      onSuccess: () => {
        queryClient.invalidateQueries(CasesQueryKeys.detail(caseId));
        onSuccess();
      },
    },
  );

  return { createDamages, isLoading, isError, error };
};

export const useEditDamages = ({ onSuccess, caseId }) => {
  const queryClient = useQueryClient();
  const {
    mutateAsync: editDamages,
    isLoading,
    isError,
    error,
  } = useMutation(
    async (payload) => {
      const damagesToSave = [];
      if (payload?.length) {
        damagesToSave.push(
          ...payload.map(({ category, status, pacNumber, pacUrl, id }) => {
            return {
              category,
              status,
              pacNumber,
              pacUrl,
              damageId: id,
            };
          }),
        );
      }
      if (damagesToSave.length) {
        // Second API call based on the result of the `newDamagesRes`
        await saveDiagnosticsCases(caseId, {
          action: 'addDamage',
          damages: damagesToSave,
        });
      }
      return damageService.patchUpdateDamages(payload);
    },
    {
      onError: (error) => {
        console.log(`there has been an error ${error}`);
      },
      onSuccess: () => {
        queryClient.refetchQueries(QueryKey.CASE_DAMAGES);
        onSuccess();
      },
    },
  );

  return { editDamages, isLoading, isError, error };
};

export const useUpdateDamage = () => {
  const { mutate } = useMutation(
    async ({ payload }) => await damageService.patchUpdateDamages([payload]),
  );
  return { mutate };
};

export const detachDamage = () => {
  const { mutate } = useMutation(
    async ({ payload }) => await damageService.patchUpdateDamages([payload]),
  );
  return { mutate };
};
